import type { ILink, IPagePath } from "@contentful-api/types/contentful";
import { pathnames } from "@i18n/routing";
import { PAGE_NAV_LINK_TEST_ID_PREFIX } from "@e2e/helpers/common/constants";
import { useLocale } from "next-intl";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

export const linkTypeGuard = (link: ILink | IPagePath): link is ILink =>
    Boolean((link as ILink)?.fields?.internalLink);

const HIDDEN_ROUTES = ["/mi-cuenta/direcciones", "/my-account/addresses"];

const LOCALE_REGEX = /^\/[a-z]{2}-[A-Z]{2}/;

const getLastSegment = (path: string): string => {
    return path.split("/").filter(Boolean).pop() || "";
};

type Config = {
    links: (ILink | IPagePath)[];
};

export const useLink = ({ links }: Config) => {
    const pathname = usePathname();
    const locale = useLocale();
    const [activeLink, setActiveLink] = useState<number | null>(null);

    const getEnglishPathname = (localizedPath: string): string => {
        const pathWithoutLocale = localizedPath.replace(LOCALE_REGEX, "");

        for (const [englishPath, localizedPaths] of Object.entries(pathnames)) {
            if (pathWithoutLocale === localizedPaths[locale]) {
                return englishPath;
            }
        }
        return pathWithoutLocale;
    };

    const getSlug = (link: ILink | IPagePath) =>
        linkTypeGuard(link)
            ? link?.fields?.internalLink?.fields?.completeSlug
            : link?.fields?.completeSlug;

    const onChangeActiveLink = (index: number) => () => setActiveLink(index);

    const isAddressesLink = (link: ILink | IPagePath) => HIDDEN_ROUTES.includes(getSlug(link));

    // Build a stable, locale-independent data-testid for a nav link by mapping its
    // (possibly localized) slug back to the English last path segment,
    // e.g. `/mi-cuenta/direcciones` -> `page-nav-link-addresses`.
    const getLinkTestId = (link: ILink | IPagePath): string | undefined => {
        const slug = getSlug(link);
        if (!slug) return undefined;

        const englishSegment = getLastSegment(getEnglishPathname(slug));
        if (!englishSegment) return undefined;

        return `${PAGE_NAV_LINK_TEST_ID_PREFIX}-${englishSegment}`;
    };

    useEffect(() => {
        const decodedPath = decodeURI(pathname);
        const englishPath = getEnglishPathname(decodedPath);

        const localizedLastSegment = getLastSegment(decodedPath);
        const englishLastSegment = getLastSegment(englishPath);

        links.forEach((link, index) => {
            const slug = getSlug(link);
            if (!slug) return;

            const slugLastSegment = getLastSegment(slug);

            if (
                slugLastSegment === localizedLastSegment ||
                slugLastSegment === englishLastSegment
            ) {
                setActiveLink(index);
            }
        });
    }, [pathname, locale, links]);

    return { activeLink, isAddressesLink, onChangeActiveLink, getLinkTestId };
};
